import { config } from "../Environment";

/**
 * API functions for AI-powered insight features
 */

export interface ExtractInsightsRequest {
  text: string;
  language?: string;
}

export interface ExtractedInsight {
  content: string;
  confidence: number;
  suggestedCategory?: string;
  suggestedTags?: string[];
}

export interface ExtractInsightsResponse {
  status: "SUCCESS" | "ERROR";
  insights: ExtractedInsight[];
  fault?: string;
}

export interface SynthesizeInsightsRequest {
  insightIds?: string[];
  period?: "week" | "month" | "quarter" | "year" | "all";
  language?: string;
}

export interface InsightTheme {
  name: string;
  insightCount: number;
  summary: string;
  insightIds: string[];
  /** Life projects / identity areas represented among this theme's insights
   *  (resolved server-side, scoped to the current synthesis period). */
  projectIds?: string[];
  spaceIds?: string[];
}

export interface SynthesizeInsightsResponse {
  status: "SUCCESS" | "ERROR";
  synthesis: {
    period: string;
    totalInsights: number;
    themes: InsightTheme[];
    recommendations: string[];
    growthAreas: string[];
  };
  fault?: string;
}

export interface GetConnectionsRequest {
  insightId: string;
  limit?: number;
}

export interface InsightConnection {
  relatedInsightId: string;
  similarity: number;
  connectionType: "semantic" | "category" | "time" | "source";
}

export interface GetConnectionsResponse {
  status: "SUCCESS" | "ERROR";
  connections: InsightConnection[];
  fault?: string;
}

export interface ConvertToActionsRequest {
  insightId: string;
}

export interface SuggestedAction {
  content: string;
  priority: "high" | "medium" | "low";
  suggestedDueDate?: number;
}

export interface ConvertToActionsResponse {
  status: "SUCCESS" | "ERROR";
  actions: SuggestedAction[];
  fault?: string;
}

export interface InsightStatsResponse {
  status: "SUCCESS" | "ERROR";
  stats: {
    total: number;
    thisMonth: number;
    lastMonth: number;
    byCategory: {
      categoryId: string;
      categoryName: string;
      categoryColor: string;
      count: number;
    }[];
    bySource: {
      source: string;
      count: number;
    }[];
    byMonth: {
      month: string;
      count: number;
    }[];
    streak: {
      current: number;
      longest: number;
      lastInsightDate?: string;
    };
  };
  fault?: string;
}

/**
 * Extract insights from text using AI
 * POST /api/ai/extract-insights
 */
export const extractInsights = async (
  retryFetch: Function,
  request: ExtractInsightsRequest
): Promise<ExtractInsightsResponse> => {
  const response = await retryFetch(
    `${config.API_BASE}/api/ai/extract-insights`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(request),
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || "Failed to extract insights");
  }

  return await response.json();
};

/**
 * Synthesize insights into themes and recommendations
 * POST /api/ai/synthesize-insights
 */
export const synthesizeInsights = async (
  retryFetch: Function,
  request: SynthesizeInsightsRequest
): Promise<SynthesizeInsightsResponse> => {
  const response = await retryFetch(
    `${config.API_BASE}/api/ai/synthesize-insights`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(request),
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || "Failed to synthesize insights");
  }

  return await response.json();
};

/**
 * Get related/connected insights
 * GET /api/insights/:id/connections
 */
export const getInsightConnections = async (
  retryFetch: Function,
  insightId: string,
  limit?: number
): Promise<GetConnectionsResponse> => {
  const params = new URLSearchParams();
  if (limit) params.append("limit", String(limit));

  const response = await retryFetch(
    `${config.API_BASE}/api/insights/${insightId}/connections${
      params.toString() ? `?${params.toString()}` : ""
    }`,
    {
      method: "GET",
      credentials: "include",
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || "Failed to get insight connections");
  }

  return await response.json();
};

/**
 * Convert an insight into suggested action items
 * POST /api/ai/insight-to-actions
 */
export const convertInsightToActions = async (
  retryFetch: Function,
  request: ConvertToActionsRequest
): Promise<ConvertToActionsResponse> => {
  const response = await retryFetch(
    `${config.API_BASE}/api/ai/insight-to-actions`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(request),
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || "Failed to convert insight to actions");
  }

  return await response.json();
};

/**
 * Get detailed insight statistics
 * GET /api/insights/stats/detailed
 */
export const getDetailedInsightStats = async (
  retryFetch: Function
): Promise<InsightStatsResponse> => {
  const response = await retryFetch(
    `${config.API_BASE}/api/insights/stats/detailed`,
    {
      method: "GET",
      credentials: "include",
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || "Failed to get insight stats");
  }

  return await response.json();
};

/**
 * Toggle favorite status for an insight
 * POST /api/insights/:id/favorite
 */
export const toggleInsightFavorite = async (
  retryFetch: Function,
  insightId: string
): Promise<{ status: "SUCCESS" | "ERROR"; isFavorite: boolean }> => {
  const response = await retryFetch(
    `${config.API_BASE}/api/insights/${insightId}/favorite`,
    {
      method: "POST",
      credentials: "include",
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || "Failed to toggle favorite");
  }

  return await response.json();
};

/**
 * Suggest categories for insight content
 */
export interface SuggestCategoriesRequest {
  content: string;
  existingCategoryIds?: string[];
}

export interface SuggestedCategory {
  categoryId?: string;       // ID if matches existing category
  categoryName: string;      // Name of category
  confidence: number;        // 0.0-1.0 confidence score
  isNew: boolean;            // True if suggesting a new category to create
  suggestedColor?: string;   // Suggested color for new categories
  reason?: string;           // Why this category was suggested
}

export interface SuggestCategoriesResponse {
  status: "SUCCESS" | "ERROR";
  suggestions: SuggestedCategory[];
  fault?: string;
}

/**
 * Get AI-suggested categories for insight content
 * POST /api/ai/suggest-categories
 */
export const suggestCategories = async (
  retryFetch: Function,
  request: SuggestCategoriesRequest
): Promise<SuggestCategoriesResponse> => {
  const response = await retryFetch(
    `${config.API_BASE}/api/ai/suggest-categories`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(request),
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || "Failed to suggest categories");
  }

  return await response.json();
};
